/**
 * Contains the mapping of sub-organizer->parent organizer. It is found in the user's JWT token.
 */
export type Hierarchy = Record<string, string>;

/**
 * Contains the hierarchy of organizers where each key is an organizer and the value are its sub-organizers.
 */
export type InverseHierarchy = Record<string, string[]>;

export function invertHierarchy(hierarchy: Hierarchy): InverseHierarchy {
  const inverse: InverseHierarchy = {};
  for (const [subOrganizer, parentOrganizer] of Object.entries(hierarchy)) {
    if (!inverse[parentOrganizer]) {
      inverse[parentOrganizer] = [];
    }
    inverse[parentOrganizer].push(subOrganizer);
  }
  return inverse;
}

/**
 * Returns a list of sub-organizers for a given organization name.
 * It recursively retrieves all sub-organizers in the hierarchy.
 */
export function getAllSubOrganizers(
  hierarchy: Hierarchy,
  orgName: string
): string[] {
  // This step is extracted so it is only done once
  const inverseHierarchy = invertHierarchy(hierarchy);
  return getSubOrganizers(inverseHierarchy, orgName);
}

function getSubOrganizers(
  inverseHierarchy: InverseHierarchy,
  orgName: string
): string[] {
  // If the orgName is not in the hierarchy, return an empty array
  if (!inverseHierarchy[orgName]) {
    return [];
  }

  // Return the list of sub-organizers for the given orgName
  return inverseHierarchy[orgName]
    .flatMap((subOrg) => {
      // Recursively get sub-organizers of sub-organizers
      return getSubOrganizers(inverseHierarchy, subOrg);
    })
    .concat(inverseHierarchy[orgName]); // Include the direct sub-organizers
}
